import { Box, Popover, Typography } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CloseIcon from "../../../../../../../../assets/images/icons/CloseIcon";
import { EditIcon2 } from "../../../../../../../../assets/images/icons/EditIcon2";
import BriaButton from "../../../../../../../../components/common/BriaButton/BriaButton";
import BriaButtonGroup from "../../../../../../../../components/common/BriaButtonGroup/BriaButtonGroup";
import BriaIconButton from "../../../../../../../../components/common/BriaIconButton/BriaIconButton";
import BriaInput from "../../../../../../../../components/common/BriaInput/BriaInput";
import BriaTooltip from "../../../../../../../../components/common/BriaTooltip/BriaTooltip";
import { useAppStore } from "../../../../../../../../hooks/useStores";
import InputLayout from "../../../../../../../../layout/InputLayout/InputLayout";
import { TgModel, defaultModel } from "../../../../../../Models/store/tg-model-store";
import styles from "./TgModelNameColumn.module.scss";

type TgModelNameColumnProps = {
	model: TgModel;
};

const TgModelNameColumn = ({ model }: TgModelNameColumnProps) => {
	const { t } = useTranslation("tg", { keyPrefix: "models.dashboard.table.name" });
	const { tgStore } = useAppStore();
	const { tgModelStore } = tgStore;
	const [anchorEl, setAnchorEl] = useState<SVGElement | HTMLButtonElement>();
	const [isNameTruncated, setIsNameTruncated] = useState(false);
	const nameRef = useRef<HTMLDivElement | null>(null);
	const [isDescriptionTruncated, setIsDescriptionTruncated] = useState(false);
	const descriptionRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		tgModelStore.modelForm = anchorEl ? model : defaultModel;
	}, [anchorEl]);

	useEffect(() => {
		const checkTruncation = () => {
			if (nameRef.current) {
				const { scrollWidth, clientWidth } = nameRef.current;
				setIsNameTruncated(scrollWidth > clientWidth);
			}
		};

		checkTruncation();
		window.addEventListener("resize", checkTruncation);
		return () => window.removeEventListener("resize", checkTruncation);
	}, [model.name]);

	useEffect(() => {
		const checkDescriptionTruncation = () => {
			if (descriptionRef.current) {
				const { scrollWidth, clientWidth } = descriptionRef.current;
				setIsDescriptionTruncated(scrollWidth > clientWidth);
			}
		};

		checkDescriptionTruncation();
		window.addEventListener("resize", checkDescriptionTruncation);
		return () => window.removeEventListener("resize", checkDescriptionTruncation);
	}, [model.description]);

	const handleOpenDialog = (event: React.MouseEvent<SVGElement | HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(undefined);
	};

	const updateModel = async () => {
		const payload: TgModel = {
			...model,
			name: tgModelStore.modelForm.name,
			description: tgModelStore.modelForm.description,
		};
		try {
			if (payload.name !== model.name || payload.description !== model.description) {
				await tgModelStore.updateModel(model.id, payload);
			}
		} catch (err) {
			console.error("Failed to update model", err);
		} finally {
			handleClose();
		}
	};

	return (
		<>
			<Box className={styles.nameContainer}>
				<Box className={clsx(styles.name, { [styles.nameTruncated]: isNameTruncated })}>
					<BriaTooltip
						title={model.name}
						isHtmlTitle={false}
						disable={!isNameTruncated}
						className={styles.nameTextWrapper}
					>
						<Box ref={nameRef} className={styles.nameText}>
							{model.name}
						</Box>
					</BriaTooltip>
					{model.status === "Completed" && (
						<BriaIconButton className={styles.iconButton} title={t("edit")} onClick={handleOpenDialog}>
							<EditIcon2 />
						</BriaIconButton>
					)}
				</Box>
				<BriaTooltip
					title={model.description || ""}
					isHtmlTitle={false}
					disable={!isDescriptionTruncated || !model.description}
				>
					<Box ref={descriptionRef} className={styles.description}>
						{model.description}
					</Box>
				</BriaTooltip>
			</Box>
			<Popover
				open={!!anchorEl}
				onClose={handleClose}
				anchorEl={anchorEl}
				anchorOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				slotProps={{ paper: { className: styles.popover } }}
			>
				<Box className={styles.contentContainer}>
					<CloseIcon onClick={handleClose} className={styles.closeIcon} />
					<Typography className={styles.title}>{t("popup.title")}</Typography>
					<InputLayout label={t("popup.name.label")}>
						<BriaInput
							placeholder={t("popup.name.placeholder")}
							value={tgModelStore.modelForm.name}
							onChange={(e) => tgModelStore.handleFormChange("name", e.target.value)}
							height="45px"
						/>
					</InputLayout>
					<InputLayout label={t("popup.description.label")}>
						<BriaInput
							placeholder={t("popup.description.placeholder")}
							value={tgModelStore.modelForm.description}
							onChange={(e) => tgModelStore.handleFormChange("description", e.target.value)}
							InputProps={{ className: styles.descriptionInput }}
							inputProps={{ maxLength: 110 }}
						/>
					</InputLayout>
					<BriaButtonGroup className={styles.buttons}>
						<BriaButton className={styles.confirmBtn} buttonType="tertiaryMedium" onClick={handleClose}>
							{t("popup.cancelBtn")}
						</BriaButton>
						<BriaButton
							className={styles.confirmBtn}
							buttonType="primaryMedium"
							onClick={updateModel}
							loading={tgModelStore.loadingUpdateModel}
						>
							{t("popup.confirmBtn")}
						</BriaButton>
					</BriaButtonGroup>
				</Box>
			</Popover>
		</>
	);
};

export default observer(TgModelNameColumn);
